import type { ExportAttempt } from '../types';

import {
  type QueryFunctionContext,
  queryOptions,
  type UseSuspenseQueryOptions,
  useSuspenseQuery,
} from '@tanstack/react-query';

import type { UUID } from '~/types/common';

import { queryKeysInvoice } from '../../queryKeys';

import { fetchInvoiceExportAttempt } from './fetchInvoiceExportAttempt';

type InvoiceExportAttemptQueryOptions = Omit<
  UseSuspenseQueryOptions<ExportAttempt>,
  'queryFn' | 'queryKey'
>;

/**
 * Generates query options for one invoice export attempt.
 *
 * @param invoiceId - Invoice identifier.
 * @param attemptId - Export attempt identifier.
 * @param options - Additional query options.
 * @returns Query options for one invoice export attempt.
 */
// fallow-ignore-next-line unused-export -- canonical query-options API for route loaders
export function getInvoiceExportAttemptQueryOptions(
  invoiceId: UUID,
  attemptId: UUID,
  options?: InvoiceExportAttemptQueryOptions,
) {
  return queryOptions({
    queryFn: (context: QueryFunctionContext) =>
      fetchInvoiceExportAttempt(invoiceId, attemptId, context),
    queryKey: queryKeysInvoice.getExportAttempt(invoiceId, attemptId),
    ...options,
  });
}

/**
 * Loads one outbound export attempt for an invoice.
 *
 * @param invoiceId - Invoice identifier.
 * @param attemptId - Export attempt identifier.
 * @param options - Additional query options.
 * @returns Invoice export attempt query result.
 * @see https://app.dev.vestigas.com/redoc#tag/Invoices/operation/get_export_attempt_invoices__invoice_id__export_attempts__attempt_id__get
 */
export function useQueryInvoiceExportAttempt(
  invoiceId: UUID,
  attemptId: UUID,
  options?: InvoiceExportAttemptQueryOptions,
) {
  return useSuspenseQuery(
    getInvoiceExportAttemptQueryOptions(invoiceId, attemptId, options),
  );
}
